import React from 'react';

export type AvatarSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl';

interface AvatarProps {
  name?: string | null;
  src?: string | null;
  size?: AvatarSize;
  ring?: boolean;
  className?: string;
  onClick?: (e: React.MouseEvent) => void;
  alt?: string;
}

// Curated palette — readable on dark surfaces, deterministic per name.
const PALETTE = [
  '#ef4444', // red
  '#f97316', // orange
  '#f59e0b', // amber
  '#84cc16', // lime
  '#10b981', // emerald
  '#14b8a6', // teal
  '#06b6d4', // cyan
  '#3b82f6', // blue
  '#6366f1', // indigo
  '#8b5cf6', // violet
  '#d946ef', // fuchsia
  '#ec4899', // pink
];

const hashName = (name: string): number => {
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) | 0;
  return Math.abs(h);
};

const getInitials = (name: string): string => {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

/**
 * Avatar — image or deterministic initial-with-color fallback.
 */
export const Avatar: React.FC<AvatarProps> = ({
  name,
  src,
  size = 'md',
  ring = false,
  className = '',
  onClick,
  alt,
}) => {
  const safeName = (name || '').trim() || '?';
  const color = PALETTE[hashName(safeName) % PALETTE.length];
  const initials = getInitials(safeName);
  const sizeClass = `warka-avatar-${size}`;
  const ringClass = ring ? 'warka-avatar-ring' : '';

  const Tag: any = onClick ? 'button' : 'span';

  return (
    <Tag
      type={onClick ? 'button' : undefined}
      onClick={onClick}
      className={`warka-avatar ${sizeClass} ${ringClass} ${className}`.trim()}
      style={src ? undefined : { backgroundColor: color }}
      aria-label={alt || safeName}
    >
      {src ? <img src={src} alt={alt || safeName} loading="lazy" /> : initials}
    </Tag>
  );
};

export default Avatar;
